import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPupCatalog, selectPupCard } from '../../redux/pupCatalogSlice';
import pupCardArt from './pupCardArt';
import { ModifierText } from './formatModifier';
import useFitText from './useFitText';
import './PupCard.css';

// One Power-Up ("Pup") card, rendered in CSS from the template components (see
// assets/pupcards/PowerUp_BoxedComponents.PNG): Name (top, full width), Picture (art image,
// with the tier letter in its top-right corner), Modifier (stage line), Description, and
// Flavor Text (bottom-left italic). `code` is the card's wire id (the PupCardType enum name
// — the only thing gameplay messages carry); the display data { name, color, modifier,
// description, flavor, artKey, ... } resolves here from the cached GET /cards/catalog
// (pupCatalogSlice). The Color picks the card's finish (CSS skin) — Silver / Gold /
// Celestial — and artKey picks the Picture artwork (shared across a card's street variants).
// The color is NOT spelled out on the face: it shows as one small italic S / G / C over the
// top-right of the Picture, which leaves the whole header row to the Name.
// `roomId` (optional) is the table this render belongs to: a card whose catalog entry
// carries a `tournamentNote` ("Remove this card from the game.") shows that sentence ONLY
// when the room is a Sit-and-Go/tournament table — the removal really happens only there;
// on cash tables the card recirculates, so the sentence would be false. No roomId (no
// table context) = the note never renders.
//
// Name and Modifier are ONE line each and the Description is bounded by its white box; the
// catalog's copy varies wildly in length, so each of the three is measured by `useFitText`
// and its font stepped down from the PupCard.css size (a maximum, not a fixed size) until it
// fits. All three are left-aligned, flush with the Picture's left edge at the furthest.

// The tier's one-letter mark. Falls back to the first letter of an unrecognized color so a
// future tier still marks the card instead of rendering blank.
const TIER_LETTERS = { silver: 'S', gold: 'G', celestial: 'C' };

const PupCard = ({ code, roomId }) => {
  const dispatch = useDispatch();
  const card = useSelector((state) => selectPupCard(state, code));
  const isTournamentTable = useSelector((state) => {
    if (roomId == null) return false;
    const game = state.games?.activeGames?.[roomId];
    return Boolean(game && (game.isSitAndGo || game.isTournament));
  });

  // Self-heal: a code the catalog can't resolve means our copy is stale (boot fetch failed,
  // or the server patched its cards mid-session) — refetch. The thunk's own 10s throttle
  // keeps a genuinely unknown code from looping this into a request storm; meanwhile the
  // placeholder frame below keeps the layout (and any countdown riding on it) intact.
  useEffect(() => {
    if (code && !card) dispatch(fetchPupCatalog({ force: true }));
  }, [dispatch, code, card]);

  const skin = (card?.color || 'silver').toLowerCase();
  const art = card ? (pupCardArt[card.artKey] || pupCardArt[card.code]) : null;
  const tierLetter = card ? (TIER_LETTERS[skin] || (card.color || '').trim().charAt(0).toUpperCase()) : '';
  const showNote = Boolean(card?.tournamentNote && isTournamentTable);

  // Re-fit whenever the rendered text could have changed. The keys carry the code AND the
  // text so a mid-session catalog patch re-measures too; the description's key also carries
  // the flavor (which shares the white box and so decides how much height is left) and the
  // tournament note (an extra line, only at Sit-and-Go/tournament tables).
  const nameRef = useFitText(`${code}|${card?.name || ''}`, 'width', 10);
  const modifierRef = useFitText(`${code}|${card?.modifier || ''}`, 'width', 8);
  // The 5px floor is headroom, not a target: the longest description in the catalog (Raging
  // Madness, ~500 characters) settles around 6.5px, so nothing reaches it today.
  const descriptionRef = useFitText(
    `${code}|${card?.description || ''}|${card?.flavor || ''}|${showNote ? card.tournamentNote : ''}`,
    'height',
    5,
  );

  return (
    <div className={`pup-card pup-card--${skin}`}>
      <div className="pup-card-header">
        <span className="pup-card-name" ref={nameRef}>{card ? card.name : ''}</span>
      </div>
      <div className="pup-card-picture">
        {art && <img src={art} alt={card.name} draggable="false" />}
        {tierLetter && <span className="pup-card-tier">{tierLetter}</span>}
      </div>
      <div className="pup-card-modifier" ref={modifierRef}>
        {card ? <ModifierText modifier={card.modifier} /> : ''}
      </div>
      <div className="pup-card-body">
        <div className="pup-card-description" ref={descriptionRef}>
          {card ? card.description : ''}
          {showNote && (
            <div className="pup-card-tournament-note">{card.tournamentNote}</div>
          )}
        </div>
        <div className="pup-card-flavor">{card ? card.flavor : ''}</div>
      </div>
    </div>
  );
};

export default PupCard;
